import { satisfies } from './semver'

// Ids the HOST itself provides, not plugins. They are always there, they are
// never in the registry, and a plugin is allowed to depend on one. Without
// this, regenerating the registry from the manifests made the whole core pack
// refuse to seed — the board loaded nothing at all — because split view says
// it needs omni.panes and no plugin supplies it.
export const HOST_PROVIDED = new Set(['omni.panes', 'omni.boards'])

// Dependency utilities over plugin manifests. Dependencies are PLUGIN ids
// with semver ranges (nothing to do with npm). Packs participate like any
// plugin: they are manifests whose members are their dependencies.

// Returns { toInstall: manifest[], errors: string[] } — the transitive closure
// of `id` that is not yet in `installedIds`, in no particular order.
export function resolveInstallClosure(id, registryById, installedIds) {
  const toInstall = []
  const errors = []
  const seen = new Set()
  const queue = [[id, null]]
  while (queue.length) {
    const [cur, wantedBy] = queue.shift()
    if (seen.has(cur)) continue
    seen.add(cur)
    if (HOST_PROVIDED.has(cur)) continue
    const manifest = registryById.get(cur)
    if (!manifest) {
      errors.push(
        wantedBy
          ? `"${wantedBy}" requires "${cur}", which is not in the registry`
          : `"${cur}" is not in the registry`
      )
      continue
    }
    if (!installedIds.has(cur) || cur === id) {
      if (!installedIds.has(cur)) toInstall.push(manifest)
    }
    for (const [depId, range] of Object.entries(manifest.dependencies ?? {})) {
      if (HOST_PROVIDED.has(depId)) continue
      const dep = registryById.get(depId)
      if (dep && !satisfies(dep.version, range)) {
        errors.push(
          `"${cur}" requires "${depId}" ${range}, but the registry has ${dep.version}`
        )
      }
      queue.push([depId, cur])
    }
  }
  return { toInstall, errors }
}

// Detects dependency cycles among the given manifests.
// Returns a cycle path like ['a','b','a'] or null.
export function findCycle(manifests) {
  const byId = new Map(manifests.map((m) => [m.id, m]))
  const WHITE = 0
  const GRAY = 1
  const BLACK = 2
  const color = new Map()
  const stack = []
  let cycle = null

  function visit(id) {
    if (cycle) return
    color.set(id, GRAY)
    stack.push(id)
    const deps = Object.keys(byId.get(id)?.dependencies ?? {})
    for (const dep of deps) {
      if (!byId.has(dep)) continue
      const c = color.get(dep) ?? WHITE
      if (c === GRAY) {
        cycle = [...stack.slice(stack.indexOf(dep)), dep]
        return
      }
      if (c === WHITE) visit(dep)
    }
    stack.pop()
    color.set(id, BLACK)
  }

  for (const m of manifests) {
    if ((color.get(m.id) ?? WHITE) === WHITE) visit(m.id)
  }
  return cycle
}

// Topologically sorts manifests into layers: everything in layer N depends
// only on layers < N, so one layer can load and activate in parallel.
// Dependencies on plugins outside `manifests` are ignored here — the caller
// decides blocked states first. optionalDependencies add soft edges when the
// optional dep is present in the set.
export function topoLayers(manifests) {
  const byId = new Map(manifests.map((m) => [m.id, m]))
  const indegree = new Map(manifests.map((m) => [m.id, 0]))
  const dependents = new Map() // depId -> Set of ids depending on it

  for (const m of manifests) {
    const all = {
      ...(m.dependencies ?? {}),
      ...(m.optionalDependencies ?? {}),
    }
    for (const depId of Object.keys(all)) {
      if (!byId.has(depId)) continue
      indegree.set(m.id, indegree.get(m.id) + 1)
      let set = dependents.get(depId)
      if (!set) {
        set = new Set()
        dependents.set(depId, set)
      }
      set.add(m.id)
    }
  }

  const layers = []
  let frontier = manifests.filter((m) => indegree.get(m.id) === 0)
  const done = new Set()
  while (frontier.length) {
    layers.push(frontier)
    const next = []
    for (const m of frontier) {
      done.add(m.id)
      for (const depId of dependents.get(m.id) ?? []) {
        const remaining = indegree.get(depId) - 1
        indegree.set(depId, remaining)
        if (remaining === 0) next.push(byId.get(depId))
      }
    }
    frontier = next
  }
  // Anything not emitted sits on a cycle. A cycle among HARD dependencies is
  // the caller's problem (findCycle reports it), but an OPTIONAL one must not
  // collapse the ordering: dumping the rest into one layer activates them all
  // at once, and a plugin then reads a service that has not been provided yet
  // and fails with a message that names the wrong thing. Re-layer the leftover
  // using hard dependencies only, which are known to be acyclic.
  const leftover = manifests.filter((m) => !done.has(m.id))
  if (leftover.length) {
    const hardOnly = leftover.map((m) => ({ ...m, optionalDependencies: undefined }))
    const cycleInHard = findCycle(hardOnly)
    if (cycleInHard) {
      layers.push(leftover)
    } else {
      const byLeftoverId = new Map(leftover.map((m) => [m.id, m]))
      for (const sub of topoLayers(hardOnly)) {
        layers.push(sub.map((m) => byLeftoverId.get(m.id)))
      }
    }
  }
  return layers
}

// Which installed+enabled plugins transitively depend on `id`?
export function transitiveDependents(id, manifests) {
  const dependents = new Map()
  for (const m of manifests) {
    for (const depId of Object.keys(m.dependencies ?? {})) {
      let set = dependents.get(depId)
      if (!set) {
        set = new Set()
        dependents.set(depId, set)
      }
      set.add(m.id)
    }
  }
  const result = new Set()
  const queue = [id]
  while (queue.length) {
    const cur = queue.shift()
    for (const dep of dependents.get(cur) ?? []) {
      if (!result.has(dep)) {
        result.add(dep)
        queue.push(dep)
      }
    }
  }
  return result
}
